import React, { useEffect, useState, useImperativeHandle, forwardRef, memo, useRef } from 'react'
import PropTypes from 'prop-types'
import { TextField } from './'

/**
 * Implements a date and time control with associated dropdowns.
 *
 * @param {object} props component props
 * @returns {DateAndTime} DateAndTime component
 */
const DateAndTime = memo(forwardRef((props, ref) => {
  const { minDate, maxDate, ...finalProps } = props
  const [value, setValue] = useState(props.value || props.defaultValue)
  const refTextField = useRef()

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: value => {
      setValue(value)
      refTextField.current.setValue(value)
      props.onChange && props.onChange(value)
    }
  }))

  useEffect(() => {
    if (props.value === undefined) {
      let today = (new Date()).toISOString()
      today = today.substr(0, today.length - 8)
      setValue(today)
    } else {
      setValue(props.value)
    }
  }, [props.value])

  return (
    <TextField
      type='datetime-local'
      value={value}
      inputProps={{
        min: minDate,
        max: maxDate
      }}
      InputLabelProps={{
        shrink: true,
        ...props.InputLabelProps
      }}
      {...finalProps}
      ref={refTextField}
    />
  )
}))

DateAndTime.propTypes = { 
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Default value for the control.
   */
  defaultValue: PropTypes.string,
  /**
   value for the control.
   */
  value: PropTypes.string,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when an options is selected.
   */
  onChange: PropTypes.func,
  /**
   Props applied to the label.
   */
  InputLabelProps: PropTypes.object,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Props applied to the Input element.
   */
  inputProps: PropTypes.object,
  /**
   Type for the control.
   */
  type: PropTypes.oneOf(['datetime-local', 'date', 'time', 'month', 'week']),
  minDate: PropTypes.string,
  maxDate: PropTypes.string
}

DateAndTime.defaultProps = {
  noTranslate: false,
  value: '',
  type: 'datetime-local'
}

export default DateAndTime
